import React, { useRef, useEffect, useState } from 'react';
import {
  Link
} from "react-router-dom";

const ProjectSideSection = (props) => {
	const scrollContainerRef = useRef(null);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);
	const [containerWidth, setContainerWidth] = useState('100%');
	const [imageWidth, setImageWidth] = useState('auto');

	const hasMultipleImages = props.images && props.images.length > 1;

	// Calculate container width based on first image dimensions
	useEffect(() => {
		if (!hasMultipleImages || !props.images || props.images.length === 0) return;

		const calculateContainerWidth = () => {
			// Method 1: Try to get width from parent grid container
			let sidebarWidth = null;
			const parentGrid = scrollContainerRef.current?.closest('.grid.flex-width-80');
			
			if (parentGrid) {
				sidebarWidth = parentGrid.offsetWidth;
			} else {
				// Method 2: Fallback to window width calculation
				// flex-width-80 typically means 80% of viewport width
				sidebarWidth = window.innerWidth * 0.8;
			}
			
			if (!sidebarWidth) return;
			
			// Make images wider than the container to enable scrolling
			const calculatedImageWidth = sidebarWidth * 0.6; // 80% of sidebar width (wider than container)
			
			// Set image width
			setImageWidth(`${calculatedImageWidth}px`);
			// Container width stays at 100% of sidebar, but images are wider
		};

		// Calculate on mount and resize
		calculateContainerWidth();
		window.addEventListener('resize', calculateContainerWidth);
		
		return () => {
			window.removeEventListener('resize', calculateContainerWidth);
		};
	}, [hasMultipleImages, props.images]);

	// Mouse drag scrolling for web (only for multiple images)
	const handleMouseDown = (e) => {
		if (!hasMultipleImages) return;
		setIsDragging(true);
		setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
		setScrollLeft(scrollContainerRef.current.scrollLeft);
	};

	const handleMouseMove = (e) => {
		if (!isDragging || !hasMultipleImages) return;
		e.preventDefault();
		const x = e.pageX - scrollContainerRef.current.offsetLeft;
		const walk = (x - startX) * 2;
		const newScrollLeft = scrollLeft - walk;
		scrollContainerRef.current.scrollLeft = newScrollLeft;
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleMouseLeave = () => {
		setIsDragging(false);
	};

	// Touch/swipe scrolling for mobile (only for multiple images)
	const handleTouchStart = (e) => {
		if (!hasMultipleImages) return;
		setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
		setScrollLeft(scrollContainerRef.current.scrollLeft);
	};

	const handleTouchMove = (e) => {
		if (!startX || !hasMultipleImages) return;
		const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
		const walk = (x - startX) * 2;
		scrollContainerRef.current.scrollLeft = scrollLeft - walk;
	};

	const handleTouchEnd = () => {
		setStartX(0);
	};

	// Add cursor style when dragging (only for multiple images)
	useEffect(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.style.cursor = hasMultipleImages ? (isDragging ? 'ew-resize' : 'ew-resize') : 'default';
		}
	}, [isDragging, hasMultipleImages]);

	// Parse category string into tags array
	const parseCategoryToTags = (category) => {
		if (!category) return [];
		return category.split('|').map(tag => tag.trim()).filter(tag => tag.length > 0);
	};

	const categoryTags = parseCategoryToTags(props.category);

	return (
	<React.Fragment>
		<style>
			{`
				.image-scroll-container::-webkit-scrollbar {
					display: none;
				}
				.image-scroll-container {
					-ms-overflow-style: none;
					scrollbar-width: none;
				}
				.scroll-image {
					transition: transform 0.2s ease;
				}
				.scroll-image:hover {
					transform: scale(1.02);
				}
				.scroll-image.single-image:hover {
					transform: none;
				}
				.category-tags .tag:nth-child(1) {
					margin-left: 0;
				}
				.category-tags .tag {
					display: inline-block;
					background-color: var(--color-gray-light);
					border: 1px solid var(--color-gray-light);
					border-radius: 5px;
					color: var(--color-black);
					font-size: 10px;
					letter-spacing: .5px;
					margin: 0 8px 8px 0;
					padding: 1px 1em;
					text-transform: uppercase;
					white-space: pre-line;
				}
				.project-box {
					margin-bottom: 0;
				}
			`}
		</style>
		<div className="project-box col-xl-6 col-md-6 col-xs-12 col-12">
			<h3 className="">{props.title}</h3>
			{categoryTags.length > 0 && (
				<div className="category-tags">
					{categoryTags.map((tag, index) => (
						<span key={index} className="tag">{tag}</span>
					))}
				</div>
			)}
			<p className="">{props.description}</p>
		</div>

		<div className="col-xl-12 col-md-12 col-xs-12 col-12">
			{props.images && props.images.length > 0 && (
				<div 
					ref={scrollContainerRef}
					className="image-scroll-container"
					style={{
						display: 'flex',
						overflowX: hasMultipleImages ? 'auto' : 'visible',
						overflowY: 'hidden', // Prevent vertical scrolling within image container
						scrollbarWidth: 'none', // Firefox
						msOverflowStyle: 'none', // IE/Edge
						WebkitOverflowScrolling: hasMultipleImages ? 'touch' : 'auto', // iOS momentum scrolling
						cursor: hasMultipleImages ? 'ew-resize' : 'default',
						userSelect: hasMultipleImages ? 'none' : 'auto',
						marginBottom: '4em',
						marginTop: '0.5em', // Reduced gap between project info and images
						// Dynamic width calculation for multiple images
						...(hasMultipleImages && {
							width: '100%', // Container takes full sidebar width
							maxWidth: '100%', // Constrain to sidebar width
							marginRight: '0'
						})
					}}
					onMouseDown={hasMultipleImages ? handleMouseDown : undefined}
					onMouseMove={hasMultipleImages ? handleMouseMove : undefined}
					onMouseUp={hasMultipleImages ? handleMouseUp : undefined}
					onMouseLeave={hasMultipleImages ? handleMouseLeave : undefined}
					onTouchStart={hasMultipleImages ? handleTouchStart : undefined}
					onTouchMove={hasMultipleImages ? handleTouchMove : undefined}
					onTouchEnd={hasMultipleImages ? handleTouchEnd : undefined}
				>
					{props.images.map((image, index) => (
						<img 
							key={index}
							className={`scroll-image ${!hasMultipleImages ? 'single-image' : ''}`}
							style={{
								height: 'auto',
								width: hasMultipleImages ? imageWidth : '100%', // Use calculated width for multiple images, 100% for single
								maxWidth: hasMultipleImages ? imageWidth : '100%', // Consistent with width
								flexShrink: 0,
								marginRight: hasMultipleImages && index < props.images.length - 1 ? '0' : '0'
							}} 
							src={image} 
							alt={`${props.title} - Image ${index + 1}`} 
						/>
					))}
				</div>
			)}
		</div>
	</React.Fragment>
	);
};

export default ProjectSideSection;